/**
 * Secure Logger Service
 * HIPAA Compliance: Prevents PHI from being logged to console
 * Regulation: 45 CFR 164.530(c) - Safeguards
 *
 * This service:
 * - Sanitizes data before logging (removes PHI)
 * - Only logs in development mode
 * - Provides structured logging with levels
 */

const isDevelopment = process.env.NODE_ENV === 'development';

// Fields that may contain PHI and should be redacted
const PHI_FIELDS = [
  // Patient identifiers
  'name', 'firstName', 'lastName', 'fullName', 'memberFullName',
  'patientName', 'memberName',
  // Dates
  'dob', 'dateOfBirth', 'birthDate',
  // Contact info
  'email', 'phone', 'phoneNumber', 'address', 'street', 'city', 'zipCode',
  // Medical identifiers
  'ssn', 'socialSecurityNumber', 'memberId', 'patientId', 'mrn',
  'medicareId', 'medicaidId', 'insuranceId',
  // Medical info
  'diagnosis', 'diagnoses', 'medications', 'allergies',
  // Notes
  'note', 'notes', 'clinicalNote', 'noteContent',
];

// Case-insensitive check if key might contain PHI
const isPHIField = (key) => {
  const lowerKey = key.toLowerCase();
  return PHI_FIELDS.some(field => lowerKey.includes(field.toLowerCase()));
};

/**
 * Recursively sanitizes an object by redacting PHI fields
 * @param {any} data - Data to sanitize
 * @param {number} depth - Current recursion depth (to prevent infinite loops)
 * @returns {any} - Sanitized data
 */
const sanitizeData = (data, depth = 0) => {
  // Prevent infinite recursion
  if (depth > 10) {
    return '[MAX_DEPTH_EXCEEDED]';
  }

  // Handle null/undefined
  if (data === null || data === undefined) {
    return data;
  }

  // Handle primitives
  if (typeof data !== 'object') {
    return data;
  }

  // Handle arrays
  if (Array.isArray(data)) {
    return data.map(item => sanitizeData(item, depth + 1));
  }

  // Handle objects
  const sanitized = {};
  for (const [key, value] of Object.entries(data)) {
    if (isPHIField(key)) {
      sanitized[key] = '[REDACTED]';
    } else if (typeof value === 'object' && value !== null) {
      sanitized[key] = sanitizeData(value, depth + 1);
    } else {
      sanitized[key] = value;
    }
  }

  return sanitized;
};

/**
 * Secure Logger API
 */
const secureLogger = {
  /**
   * Log info message (only in development)
   * @param {string} message - Log message
   * @param {any} data - Optional data (will be sanitized)
   */
  info: (message, data) => {
    if (isDevelopment) {
      if (data !== undefined) {
        console.log(`[INFO] ${message}`, sanitizeData(data));
      } else {
        console.log(`[INFO] ${message}`);
      }
    }
  },

  /**
   * Log warning message
   * @param {string} message - Warning message
   * @param {any} data - Optional data (will be sanitized)
   */
  warn: (message, data) => {
    if (isDevelopment) {
      if (data !== undefined) {
        console.warn(`[WARN] ${message}`, sanitizeData(data));
      } else {
        console.warn(`[WARN] ${message}`);
      }
    }
  },

  /**
   * Log error message (logs in all environments, but sanitized)
   * @param {string} message - Error message
   * @param {Error|any} error - Error object or data
   */
  error: (message, error) => {
    // Always log errors, but sanitize them
    if (error instanceof Error) {
      console.error(`[ERROR] ${message}`, {
        name: error.name,
        message: error.message,
        // Don't include stack in production
        ...(isDevelopment && { stack: error.stack })
      });
    } else if (error !== undefined) {
      console.error(`[ERROR] ${message}`, sanitizeData(error));
    } else {
      console.error(`[ERROR] ${message}`);
    }
  },

  /**
   * Log debug message (only in development)
   * @param {string} message - Debug message
   * @param {any} data - Optional data (will be sanitized)
   */
  debug: (message, data) => {
    if (isDevelopment) {
      if (data !== undefined) {
        console.debug(`[DEBUG] ${message}`, sanitizeData(data));
      } else {
        console.debug(`[DEBUG] ${message}`);
      }
    }
  },

  /**
   * Log that patient data was accessed (never logs actual PHI)
   * @param {string} action - Action performed
   * @param {string} patientId - Patient ID (will be partially masked)
   */
  patientAccess: (action, patientId) => {
    if (isDevelopment) {
      // Mask all but last 4 characters of patient ID
      const maskedId = patientId
        ? `***${patientId.slice(-4)}`
        : '[NO_ID]';
      console.log(`[PATIENT_ACCESS] ${action} - Patient: ${maskedId}`);
    }
  },

  /**
   * Create a scoped logger with a prefix
   * @param {string} scope - Logger scope (e.g., component name)
   * @returns {Object} - Scoped logger
   */
  scope: (scope) => ({
    info: (message, data) => secureLogger.info(`[${scope}] ${message}`, data),
    warn: (message, data) => secureLogger.warn(`[${scope}] ${message}`, data),
    error: (message, error) => secureLogger.error(`[${scope}] ${message}`, error),
    debug: (message, data) => secureLogger.debug(`[${scope}] ${message}`, data),
  })
};

export default secureLogger;
